import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

function isActiveSubscriber(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.subscription_status === 'active') return true;
  if (user.institution_id) return true;
  return false;
}

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await register(email, password, name);
      toast.success('Account created! Choose a subscription to unlock the full catalogue.');
      // New accounts are always non-subscribed → route based on intent
      if (redirectTo) {
        if (isActiveSubscriber(userData)) {
          navigate(redirectTo);
        } else {
          navigate(`/subscription?redirect=${encodeURIComponent(redirectTo)}`);
        }
      } else {
        navigate('/subscription');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const loginHref = redirectTo
    ? `/login?redirect=${encodeURIComponent(redirectTo)}`
    : '/login';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="register-page">
      <Navbar />
      <div className="pt-24 flex items-center justify-center px-6" style={{ minHeight: 'calc(100vh - 6rem)' }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
            The Case Verse
          </Link>
          <p className="mt-2 text-slate-600">Create your free account</p>
        </div>
        
        <div className="bg-white p-8 rounded border border-slate-200 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                placeholder="John Doe"
                data-testid="name-input"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                placeholder="Create a password"
                data-testid="password-input"
              />
            </div>
            
            <div className="bg-slate-50 p-4 rounded border border-slate-200">
              <p className="text-sm text-slate-600">
                • Free account &mdash; no card required to register
                <br />
                • Search the full catalogue of premium cases
                <br />
                • Choose an academic or corporate subscription to unlock full content
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to={loginHref} className="text-orange-600 hover:text-orange-700 font-medium" data-testid="login-link">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}